import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
const stages = ['Leyendo tu patrón principal…', 'Detectando el "programa" que más te drena…', 'Identificando tu punto ciego actual…', 'Eligiendo tu primer Código (paso #1)…', 'Construyendo tu mini-plan de 7 días…'];
interface LoadingScreenProps {
  onComplete: () => void;
}
export function LoadingScreen({
  onComplete
}: LoadingScreenProps) {
  const [completedStages, setCompletedStages] = useState(0);
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    stages.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setCompletedStages(i + 1);
      }, 1200 + i * 1100));
    });
    timers.push(setTimeout(() => {
      onComplete();
    }, 1200 + stages.length * 1100 + 800));
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);
  const overallProgress = completedStages / stages.length * 100;
  return <div className="flex min-h-screen flex-col items-center justify-center bg-matrix-bg px-5" data-id="element-96">
      <div className="mx-auto w-full max-w-md text-center" data-id="element-97">
        {/* Pulsing glow */}
        <motion.div animate={{
        scale: [1, 1.15, 1],
        opacity: [0.3, 0.6, 0.3]
      }} transition={{
        duration: 2.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="mx-auto mb-8 h-20 w-20 rounded-full bg-matrix-gold/10 flex items-center justify-center" data-id="element-98">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} className="h-10 w-10 rounded-full border-2 border-matrix-gold/30 border-t-matrix-gold" data-id="element-99" />
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="font-heading text-2xl font-bold text-matrix-text md:text-3xl" data-id="element-100">
          Analizando tu Código…
        </motion.h2>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="mt-3 font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-101">
          Tus respuestas muestran patrones muy claros. Estamos armando tu Mapa
          del Jugador&#8482; para que sepas qué hacer primero.
        </motion.p>

        {/* Progress bar */}
        <div className="mt-8 h-1.5 w-full overflow-hidden rounded-full bg-matrix-border/60" data-id="element-102">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-matrix-gold-muted to-matrix-gold" animate={{
          width: `${overallProgress}%`
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-103" />
        </div>

        {/* Stages checklist */}
        <div className="mt-8 space-y-3 text-left" data-id="element-104">
          {stages.map((stage, i) => {
          const isComplete = i < completedStages;
          const isActive = i === completedStages;
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: i <= completedStages ? 1 : 0.25,
            x: 0
          }} transition={{
            delay: 0.8 + i * 0.15,
            duration: 0.4
          }} className="flex items-center gap-3" data-id="element-105">
                {isComplete ? <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15
            }} data-id="element-106">
                    <CheckCircle2 className="h-5 w-5 text-matrix-green" data-id="element-107" />
                  </motion.div> : isActive ? <Loader2 className="h-5 w-5 animate-spin text-matrix-gold" data-id="element-108" /> : <div className="h-5 w-5 rounded-full border border-matrix-border/60" data-id="element-109" />}
                <span className={`font-body text-sm ${isComplete ? 'text-matrix-text' : isActive ? 'text-matrix-gold' : 'text-matrix-text-dim'}`} data-id="element-110">
                  {stage}
                  {isComplete && <motion.span initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="ml-2 text-matrix-green" data-id="element-111">
                      &#10003;
                    </motion.span>}
                </span>
              </motion.div>;
        })}
        </div>

        {/* Teaser */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: completedStages >= 3 ? 1 : 0
      }} transition={{
        duration: 0.8
      }} className="mt-10 font-body text-xs italic text-matrix-gold-muted" data-id="element-112">
          En segundos vas a ver por qué has sentido que "algo falta"… y cómo
          empezar a cambiarlo con un paso simple.
        </motion.p>

        <p className="mt-6 font-body text-[10px] text-matrix-text-dim" data-id="element-113">
          Esto no es un diagnóstico médico. Es un mapa de consciencia basado en
          tu auto-reporte.
        </p>
      </div>
    </div>;
}